import React, { useState, useEffect } from 'react';
import { __ } from './translations';
import {
    Building,
    FileText,
    Mail,
    Users,
    Download,
    PencilLine,
    UserPlus,
    UserMinus,
    Save,
    X,
    AlertCircle
} from 'lucide-react';
import UserInvitationNotification from './UserInvitationNotification';

interface CompanyData {
    companyId: number;
    companyName: string;
    city: string;
    postalCode: string;
    street: string;
    number: string;
    nip: string;
    companyEmail: string;
    missing_nip?: boolean;
    recipientName?: string;
    recipientCity?: string;
    recipientPostalCode?: string;
    recipientStreet?: string;
    recipientNumber?: string;
    recipientNip?: string;
    recipientEmail?: string;
    memberType: 'regular' | 'supporter';
    assignedUsers?: Array<{
        ID?: number;
        email: string;
        displayName?: string;
        isPrimary?: boolean;
    }>;
}

interface DocumentData {
    statute?: { url: string };
    statuteText?: string;
    regulations?: { url: string };
    regulationsText?: string;
    membershipBenefits?: { url: string };
    membershipBenefitsText?: string;
    resolution?: { url: string };
    resolutionText?: string;
}

// FormField component for consistency
const FormField: React.FC<{
    id: string;
    label: string;
    type?: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    required?: boolean;
    pattern?: string;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
}> = ({
    id,
    label,
    type = "text",
    value,
    onChange,
    required = false,
    pattern,
    placeholder,
    className,
    disabled = false
}) => (
    <div className={`mb-4 ${className || ''}`}>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input
            type={type}
            id={id}
            name={id}
            value={value}
            onChange={onChange}
            required={required}
            pattern={pattern}
            placeholder={placeholder}
            disabled={disabled}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
        />
    </div>
);

const EditCompanyInfo: React.FC<{
    companyData: CompanyData;
    documentData: DocumentData;
    nonce: string;
    restURL: string;
    memberType: string;
}> = ({ companyData, documentData, nonce, restURL, memberType }) => {
    const { statute, statuteText, regulations, regulationsText, resolution, resolutionText } = documentData;
    
    const [isEditing, setIsEditing] = useState(false);
    const [hasRecipient, setHasRecipient] = useState(!!companyData.recipientName);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [emailResults, setEmailResults] = useState(null);
    const [loading, setLoading] = useState(false);
console.log(companyData, memberType);
    // Company information states
    const [formData, setFormData] = useState({
        // Company Information
        companyName: companyData?.companyName || '',
        nip: companyData?.nip || '',
        city: companyData?.city || '',
        postalCode: companyData?.postalCode || '',
        street: companyData?.street || '',
        number: companyData?.number || '',
        companyEmail: companyData?.companyEmail || '',
        memberType: memberType,

        // Recipient Information
        recipientName: companyData?.recipientName || '',
        recipientCity: companyData?.recipientCity || '',
        recipientPostalCode: companyData?.recipientPostalCode || '',
        recipientStreet: companyData?.recipientStreet || '',
        recipientNumber: companyData?.recipientNumber || '',
        recipientNip: companyData?.recipientNip || '',
        recipientEmail: companyData?.recipientEmail || ''
    });

    // User management states
    const [userEmails, setUserEmails] = useState<string[]>(() => {
        const initialUserEmails = companyData.assignedUsers
            ? companyData.assignedUsers.map(user => user.email)
            : [];
        return initialUserEmails.length > 0 ? initialUserEmails : [''];
    });
    
    const [newUserEmail, setNewUserEmail] = useState('');
    const [removedUsers, setRemovedUsers] = useState<string[]>([]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleRecipientToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        setHasRecipient(e.target.checked);
        if (!e.target.checked) {
            // Clear recipient fields when unchecked
            setFormData(prev => ({
                ...prev,
                recipientName: '',
                recipientCity: '',
                recipientPostalCode: '',
                recipientStreet: '',
                recipientNumber: '',
                recipientNip: '',
                recipientEmail: ''
            }));
        }
    };

    const handleUserRemove = (index: number) => {
        const emailToRemove = userEmails[index];
        
        // Don't remove primary email
        if (emailToRemove === formData.companyEmail) {
            setError(__('Cannot remove the primary email. Please select a different primary email first.'));
            return;
        }
        
        if (emailToRemove) {
            if (window.confirm(__('Are you sure you want to remove this user?'))) {
                setRemovedUsers(prev => [...prev, emailToRemove]);
                const newUserEmails = [...userEmails];
                newUserEmails.splice(index, 1);
                setUserEmails(newUserEmails);
            }
        } else {
            // If the email is empty, just remove it without confirmation
            const newUserEmails = [...userEmails];
            newUserEmails.splice(index, 1);
            setUserEmails(newUserEmails);
        }
    };

    const handleUserAdd = () => {
        if (userEmails.length >= 5) {
            setError(__('You can only add up to 3 users.'));
            return;
        }
        if (newUserEmail && !userEmails.includes(newUserEmail)) {
            setUserEmails([...userEmails, newUserEmail]);
            setNewUserEmail('');
        } else if (!newUserEmail) {
            setUserEmails([...userEmails, '']);
        }
    };

    const handleUserEmailChange = (index: number, value: string) => {
        const newEmails = [...userEmails];
        newEmails[index] = value;
        setUserEmails(newEmails);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccessMessage(null);
        setLoading(true);

        // Filter out empty emails
        const filteredEmails = userEmails.filter(email => email.trim() !== '');

        // Validate that the primary email exists in userEmails
        if (formData.companyEmail && !filteredEmails.includes(formData.companyEmail)) {
            setError(__('The primary email must be included in the list of user emails.'));
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${restURL}board-member/v1/update-company`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': nonce,
                },
                body: JSON.stringify({
                    company_id: companyData.companyId,
                    company_data: {
                        ...formData,
                        assignedUsers: filteredEmails
                    },
                    removed_users: removedUsers,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to update company information');
            }

            const result = await response.json();
            
            if (result.success) {
                // Update company data with new values
                Object.assign(companyData, {
                    companyName: formData.companyName,
                    city: formData.city,
                    postalCode: formData.postalCode,
                    street: formData.street,
                    number: formData.number,
                    nip: formData.nip,
                    companyEmail: formData.companyEmail,
                    recipientName: hasRecipient ? formData.recipientName : '',
                    recipientCity: hasRecipient ? formData.recipientCity : '',
                    recipientPostalCode: hasRecipient ? formData.recipientPostalCode : '',
                    recipientStreet: hasRecipient ? formData.recipientStreet : '',
                    recipientNumber: hasRecipient ? formData.recipientNumber : '',
                    recipientNip: hasRecipient ? formData.recipientNip : '',
                    recipientEmail: hasRecipient ? formData.recipientEmail : '',
                });
                
                // Update assigned users
                if (result.data && result.data.assignedUsers) {
                    companyData.assignedUsers = result.data.assignedUsers;
                } else {
                    // If no assigned users returned, create from emails
                    companyData.assignedUsers = filteredEmails.map(email => ({
                        email,
                        isPrimary: email === formData.companyEmail
                    }));
                }
                
                setSuccessMessage(__('Company information updated successfully!'));
                setIsEditing(false);
                
                // Set email results if there were new users added
                if (result.data && result.data.emailResults && 
                    result.data.emailResults.details && 
                    result.data.emailResults.details.length > 0) {
                    setEmailResults(result.data.emailResults);
                }
            } else {
                setError(result.message || 'Error updating company information');
            }
        } catch (error) {
            setError(error instanceof Error ? error.message : 'An error occurred');
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    if (!isEditing) {
        return (
            <div className="space-y-6">
                {/* Warning for missing NIP */}
                {companyData.missing_nip && (
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <AlertCircle className="h-5 w-5 text-yellow-400" />
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-yellow-700">
                                    {__('Please update your company information with the NIP number.')}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Success message */}
                {successMessage && (
                    <div className="bg-green-50 border-l-4 border-green-400 p-4">
                        <div className="flex">
                            <div className="ml-3">
                                <p className="text-sm text-green-700">{successMessage}</p>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Email results notification */}
                {emailResults && (
                    <UserInvitationNotification
                        emailResults={emailResults}
                        onDismiss={() => setEmailResults(null)}
                    />
                )}
                
                {/* Main Info Card */}
                <div className="bg-white shadow rounded-lg overflow-hidden">
                    <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                        <div className="flex justify-between items-center">
                            <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                                <Building className="h-5 w-5 text-gray-500" />
                                {__('Company Information')}
                            </h2>
                            <button
                                onClick={() => setIsEditing(true)}
                                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                <PencilLine className="h-4 w-4 mr-2" />
                                {__('Edit')}
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
                        {/* Company Details */}
                        <div className="lg:col-span-2 space-y-6">
                            <div className="bg-white rounded-lg border p-4">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">
                                    {companyData.companyName}
                                </h3>
                                <div className="space-y-3">
                                    <p className="text-sm text-gray-500">{__('Full Address:')}</p>
                                    <p className="text-gray-900">
                                        {companyData.street} {companyData.number}
                                        <br />
                                        {companyData.postalCode} {companyData.city}
                                    </p>
                                    <div className="pt-2">
                                        <p className="text-sm text-gray-500">{__('NIP:')}</p>
                                        <p className="text-gray-900">{companyData.nip}</p>
                                    </div>
                                    <div className="pt-2">
                                        <p className="text-sm text-gray-500">{__('Primary Email:')}</p>
                                        <p className="text-gray-900">{companyData.companyEmail}</p>
                                    </div>
                                </div>
                            </div>

                            {companyData.recipientName && (
                                <div className="bg-white rounded-lg border p-4">
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                                        {__('Recipient Information')}
                                    </h3>
                                    <div className="space-y-3">
                                        <div>
                                            <p className="text-sm text-gray-500">{__('Name:')}</p>
                                            <p className="text-gray-900">{companyData.recipientName}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">{__('Address:')}</p>
                                            <p className="text-gray-900">
                                                {companyData.recipientStreet} {companyData.recipientNumber}
                                                <br />
                                                {companyData.recipientPostalCode} {companyData.recipientCity}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">{__('NIP:')}</p>
                                            <p className="text-gray-900">{companyData.recipientNip}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">{__('Email:')}</p>
                                            <p className="text-gray-900">{companyData.recipientEmail}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Documents Section */}
                        <div className="space-y-6">
                            <div className="bg-gray-50 rounded-lg p-4">
                                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                                    <FileText className="h-5 w-5 text-gray-500" />
                                    {__('Documents')}
                                </h3>
                                <div className="space-y-4">
                                    {Object.entries({
                                        'Statute': documentData.statute,
                                        'Regulations': documentData.regulations,
                                        'Resolution': documentData.resolution,
                                       // 'Membership Benefits': documentData.membershipBenefits
                                    }).map(([type, doc]) => (
                                        <div key={type} className="p-3 bg-white rounded shadow-sm">
                                            <h4 className="font-medium text-gray-900 mb-2">{__(type)}</h4>
                                            {doc ? (
                                                <a
                                                    href={doc.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center text-blue-600 hover:text-blue-800"
                                                >
                                                    <Download className="h-4 w-4 mr-1" />
                                                    {__(`Download ${type}`)}
                                                </a>
                                            ) : (
                                                <p className="text-sm text-gray-500">
                                                    {__(`No ${type.toLowerCase()} available`)}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Users Section */}
                            <div className="bg-white rounded-lg border p-4">
                                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                                    <Users className="h-5 w-5 text-gray-500" />
                                    {__('Assigned Users')}
                                </h3>
                                <div className="space-y-2">
                                    {companyData.assignedUsers?.map((user, index) => (
                                        <div key={index} className="flex items-center space-x-2">
                                            <Mail className="h-4 w-4 text-gray-400" />
                                            <span className="text-gray-900">{user.email}</span>
                                            {user.isPrimary && (
                                                <span className="bg-blue-100 text-blue-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded">
                                                    {__('Primary')}
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error message */}
            {error && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4">
                    <div className="flex">
                        <div className="ml-3">
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Company Information */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                        {__('Company Information')}
                    </h3>
                </div>

                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="md:col-span-2">
                            <FormField
                                id="companyName"
                                name="companyName"
                                label={__('Company Name')}
                                value={formData.companyName}
                                onChange={handleInputChange}
                                required
                                placeholder={__('Enter company name')}
                            />
                        </div>

                        <FormField
                            id="city"
                            name="city"
                            label={__('City')}
                            value={formData.city}
                            onChange={handleInputChange}
                            required
                            placeholder={__('Enter city name')}
                        />

                        <FormField
                            id="postalCode"
                            name="postalCode"
                            label={__('Postal Code')}
                            value={formData.postalCode}
                            onChange={handleInputChange}
                            required
                            pattern="\d{2}-\d{3}"
                            placeholder="00-000"
                        />

                        <FormField
                            id="street"
                            name="street"
                            label={__('Street')}
                            value={formData.street}
                            onChange={handleInputChange}
                            required
                            placeholder={__('Enter street name')}
                        />

                        <FormField
                            id="number"
                            name="number"
                            label={__('Building Number')}
                            value={formData.number}
                            onChange={handleInputChange}
                            required
                            placeholder={__('Enter building number')}
                        />

                        <FormField
                            id="nip"
                            name="nip"
                            label={__('NIP')}
                            value={formData.nip}
                            onChange={handleInputChange}
                            required
                            pattern="\d{10}"
                            placeholder="0000000000"
                        />

                        <div className="md:col-span-2">
                            <div className="mb-4">
                                <label
                                    htmlFor="companyEmail"
                                    className="block text-sm font-medium text-gray-700 mb-1"
                                >
                                    {__('Primary Email')}
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <select
                                    id="companyEmail"
                                    name="companyEmail"
                                    value={formData.companyEmail}
                                    onChange={handleInputChange}
                                    className="block w-full px-3 py-2 rounded-md border border-gray-300 
                                             shadow-sm focus:border-blue-500 focus:ring-blue-500 
                                             sm:text-sm bg-white"
                                    required
                                >
                                    <option value="">{__('Select primary email')}</option>
                                    {userEmails.filter(email => email.trim() !== '').map((email, index) => (
                                        <option key={index} value={email}>
                                            {email}
                                        </option>
                                    ))}
                                </select>
                                {userEmails.filter(email => email.trim() !== '').length === 0 && (
                                    <p className="mt-1 text-sm text-red-600">
                                        {__('You need to add at least one user email first')}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recipient Information Toggle */}
            <div className="bg-white shadow rounded-lg overflow-hidden mt-6">
                <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-medium text-gray-900">
                            {__('Recipient Information')}
                        </h3>
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="has-recipient"
                                checked={hasRecipient}
                                onChange={handleRecipientToggle}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label
                                htmlFor="has-recipient"
                                className="ml-2 text-sm text-gray-900"
                            >
                                {__('Different Recipient Information')}
                            </label>
                        </div>
                    </div>
                </div>

                {hasRecipient && (
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="md:col-span-2">
                                <FormField
                                    id="recipientName"
                                    name="recipientName"
                                    label={__('Recipient Name')}
                                    value={formData.recipientName}
                                    onChange={handleInputChange}
                                    required={hasRecipient}
                                    placeholder={__('Enter recipient name')}
                                />
                            </div>

                            <FormField
                                id="recipientCity"
                                name="recipientCity"
                                label={__('City')}
                                value={formData.recipientCity}
                                onChange={handleInputChange}
                                required={hasRecipient}
                                placeholder={__('Enter city name')}
                            />

                            <FormField
                                id="recipientPostalCode"
                                name="recipientPostalCode"
                                label={__('Postal Code')}
                                value={formData.recipientPostalCode}
                                onChange={handleInputChange}
                                required={hasRecipient}
                                pattern="\d{2}-\d{3}"
                                placeholder="00-000"
                            />

                            <FormField
                                id="recipientStreet"
                                name="recipientStreet"
                                label={__('Street')}
                                value={formData.recipientStreet}
                                onChange={handleInputChange}
                                required={hasRecipient}
                                placeholder={__('Enter street name')}
                            />

                            <FormField
                                id="recipientNumber"
                                name="recipientNumber"
                                label={__('Building Number')}
                                value={formData.recipientNumber}
                                onChange={handleInputChange}
                                required={hasRecipient}
                                placeholder={__('Enter building number')}
                            />

                            <FormField
                                id="recipientNip"
                                name="recipientNip"
                                label={__('NIP')}
                                value={formData.recipientNip}
                                onChange={handleInputChange}
                                required={hasRecipient}
                                pattern="\d{10}"
                                placeholder="0000000000"
                            />

                            <div className="md:col-span-2">
                                <FormField
                                    id="recipientEmail"
                                    name="recipientEmail"
                                    label={__('Email')}
                                    type="email"
                                    value={formData.recipientEmail}
                                    onChange={handleInputChange}
                                    placeholder={__('Enter recipient email')}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* User Management */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-medium text-gray-900">
                            {__('Assigned Users')}
                        </h3>
                        <button
                            type="button"
                            onClick={handleUserAdd}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            <UserPlus className="h-4 w-4 mr-2" />
                            {__('Add User')}
                        </button>
                    </div>
                </div>
                
                <div className="p-6 space-y-4">
                    {/* <div className="flex gap-2 mb-3 bg-gray-50 p-2 rounded">
                        <input
                            type="email"
                            value={newUserEmail}
                            onChange={(e) => setNewUserEmail(e.target.value)}
                            placeholder={__('Enter user email')}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <button
                            type="button"
                            onClick={handleUserAdd}
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            <UserPlus className="w-4 h-4 mr-2" />
                            {__('Add User')}
                        </button>
                    </div> */}
                
                    {userEmails.map((email, index) => (
                        <div key={index} className="flex items-center space-x-2 bg-gray-50 p-2 rounded">
                            <input 
                                type="email"
                                value={email}
                                onChange={(e) => handleUserEmailChange(index, e.target.value)}
                                placeholder={__('Enter user email')}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            />
                            <button
                                type="button"
                                onClick={() => handleUserRemove(index)}
                                disabled={email === formData.companyEmail}
                                className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white 
                                ${email === formData.companyEmail
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'}`}
                                title={email === formData.companyEmail ? __('Cannot remove primary email') : __('Remove User')}
                            >
                                <UserMinus className="h-4 w-4 mr-1" />
                                {__('Remove')}
                            </button>
                        </div>
                    ))}

                    {formData.companyEmail && (
                        <div className="flex items-center text-blue-600 text-sm mt-2">
                            <AlertCircle className="h-4 w-4 mr-2" />
                            {__('The primary email cannot be removed')}
                        </div>
                    )}
                </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t">
                <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                    <X className="h-4 w-4 mr-2" />
                    {__('Cancel')}
                </button>
                <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                    {loading ? (
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    ) : (
                        <Save className="h-4 w-4 mr-2" />
                    )}
                    {loading ? __('Saving...') : __('Save Changes')}
                </button>
            </div>
        </form>
    );
};

export default EditCompanyInfo;